import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "@tanstack/react-router";
import { SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import {
  ChevronDown,
  Menu,
  X,
  MonitorPlay,
  RefreshCw,
  Search,
  Sparkles,
  Video,
  Megaphone,
  Palette,
  ShieldCheck,
  Gauge,
  LayoutTemplate,
  MessageSquare,
  Wand2,
  AlertTriangle,
  User,
  Briefcase,
  DollarSign,
  HelpCircle,
  Mail,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { scrollToAnchor } from "@/lib/scroll-to-anchor";
import wendelAvatar from "@/assets/wendel-santos-avatar.webp";
import wendelAvatarGrey from "@/assets/wendel-santos-avatar-grey.webp";

export interface NavigationItem {
  label: string;
  href?: string;
  hasDropdown?: boolean;
  onClick?: () => void;
}

interface HeroNavProps {
  logo?: React.ReactNode;
  navigation?: NavigationItem[];
  ctaButton?: { label: string; onClick?: () => void; href?: string };
  mobilePricingHref?: string;
}

const PRICING_GROUPS = [
  {
    label: "Build",
    items: [
      {
        href: "/websites",
        icon: MonitorPlay,
        title: "Web Design",
        subtitle: "(one-time projects)",
      },
      {
        href: "/design-subscription",
        icon: RefreshCw,
        title: "Design Subscription",
        subtitle: "(monthly)",
      },
      { href: "/branding", icon: Palette, title: "Branding", subtitle: "(identity & strategy)" },
    ],
  },
  {
    label: "Grow",
    items: [
      { href: "/seo", icon: Search, title: "SEO", subtitle: "(get found on Google)" },
      { href: "/paid-ads", icon: Megaphone, title: "Paid Ads", subtitle: "(traffic on demand)" },
      {
        href: "/ai-services",
        icon: Sparkles,
        title: "AI Solutions",
        subtitle: "(automate the busywork)",
      },
    ],
  },
  {
    label: "Maintain",
    items: [
      {
        href: "/1-on-1-website-help",
        icon: Video,
        title: "1-on-1 Website Help",
        subtitle: "(live screen-share sessions)",
      },
      {
        href: "/care-plans",
        icon: ShieldCheck,
        title: "Care Plans",
        subtitle: "(we keep it running)",
      },
      {
        href: "/site-speed",
        icon: Gauge,
        title: "Speed & Performance",
        subtitle: "(make it load fast)",
      },
    ],
  },
];

export function HeroNav({
  logo = "Studio",
  navigation = [],
  ctaButton,
  mobilePricingHref,
}: HeroNavProps) {
  const navigate = useNavigate();
  const [pricingOpen, setPricingOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePricingOpen, setMobilePricingOpen] = useState(false);

  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Measured height of the inline (non-sticky) header, used for the spacer so
  // switching states never changes document height and nudges scroll position.
  const inlineRef = useRef<HTMLDivElement | null>(null);
  const [inlineHeight, setInlineHeight] = useState(60);

  useEffect(() => {
    const el = inlineRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setInlineHeight(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrolled]);

  useEffect(() => {
    setMounted(true);

    // Hysteresis: enter sticky above ENTER, leave only below EXIT. The gap
    // prevents the state from oscillating around a single threshold.
    const ENTER = 96;
    const EXIT = 32;
    let frame: number | null = null;
    const stickyRef = { current: false };

    const evaluate = () => {
      frame = null;
      const y = window.scrollY;
      const next = stickyRef.current ? y > EXIT : y > ENTER;
      if (next !== stickyRef.current) {
        stickyRef.current = next;
        setScrolled(next);
      }
    };

    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);


  const openPricing = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setPricingOpen(true);
  };
  const schedulePricingClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setPricingOpen(false), 120);
  };

  const nav = (
    <header
      className={cn(
        "relative flex w-full items-center justify-between transition-all duration-300",
        scrolled
          ? "max-w-6xl rounded-full border border-border/40 bg-background/60 px-4 py-1.5 shadow-lg backdrop-blur-xl supports-[backdrop-filter]:bg-background/50 lg:px-6"
          : "max-w-7xl px-6 py-5 lg:px-10",
      )}
    >
      {pricingOpen && (
        <div
          className="absolute left-1/2 top-full z-40 -translate-x-1/2 pt-4"
          onMouseEnter={openPricing}
          onMouseLeave={schedulePricingClose}
        >
          <div className="grid w-max grid-cols-3 rounded-3xl border border-border bg-background/95 px-2 py-5 shadow-xl backdrop-blur-md divide-x divide-border">
            {PRICING_GROUPS.map((group) => (
              <div key={group.label} className="flex flex-col px-10 text-left">
                <div className="px-3 pb-2 text-left">
                  <span className="block text-left text-lg font-bold" style={{ color: "#3972ee" }}>
                    {group.label}
                  </span>
                  <span className="mt-1 block h-px w-full bg-border" />
                </div>
                {group.items.map(({ href, icon: Icon, title, subtitle }) => (
                  <a
                    key={href}
                    href={href}
                    onClick={() => setPricingOpen(false)}
                    className="group flex min-w-[15rem] items-center gap-3 rounded-2xl px-3 py-2 text-left transition-colors hover:bg-accent whitespace-nowrap"
                  >
                    <span
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: "#f9f3fe" }}
                    >
                      <Icon className="h-7 w-7" strokeWidth={2.25} style={{ color: "#3972ee" }} />
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-base font-bold text-foreground">{title}</span>
                      <span className="text-sm text-muted-foreground">{subtitle}</span>
                    </span>
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={cn("flex items-center", scrolled && "max-w-[90px]")}>
        <Link
          to="/"
          aria-label="Go to homepage"
          className="flex items-center"
          onClick={(e) => {
            if (window.location.pathname === "/") {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          {typeof logo === "string" ? (
            <span
              className={cn("font-semibold tracking-tight", scrolled ? "text-base" : "text-xl")}
            >
              {logo}
            </span>
          ) : (
            logo
          )}
        </Link>
      </div>

      <nav className="hidden items-center gap-10 lg:flex">
        {navigation.map((item) => {
          const label = item.label.toLowerCase();
          const isPricing = label === "services" && item.hasDropdown !== false;
          if (isPricing) {
            return (
              <div
                key={item.label}
                className="relative cursor-pointer"
                onMouseEnter={openPricing}
                onMouseLeave={schedulePricingClose}
              >
                <button
                  type="button"
                  onClick={() => setPricingOpen((v) => !v)}
                  className={cn(
                    "group inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 font-semibold text-foreground/80 transition-colors hover:text-foreground whitespace-nowrap",
                    scrolled ? "text-sm" : "text-base",
                  )}
                >
                  {item.label}
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform ${pricingOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
            );
          }

          return (
            <a
              key={item.label}
              href={item.href ?? "#"}
              onClick={(e) => {
                if (item.onClick) {
                  e.preventDefault();
                  item.onClick();
                  return;
                }
                const href = item.href ?? "";
                if (href.startsWith("#")) {
                  e.preventDefault();
                  scrollToAnchor(href.slice(1));
                }
              }}
              className={cn(
                "group inline-flex items-center gap-1 font-semibold text-foreground/80 transition-colors hover:text-foreground whitespace-nowrap",
                scrolled ? "text-sm" : "text-base",
              )}
            >
              {item.label}
              {item.hasDropdown && (
                <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
              )}
            </a>
          );
        })}
      </nav>

      <div className="flex items-center gap-4">
        {ctaButton && (
          <a
            href={ctaButton.href ?? "#"}
            onClick={(e) => {
              if (ctaButton.onClick) {
                e.preventDefault();
                ctaButton.onClick();
              }
            }}
            className={cn(
              "hidden sm:inline-flex items-center gap-3 rounded-full pr-5 text-sm font-semibold transition-colors",
              scrolled
                ? "bg-primary text-primary-foreground hover:bg-primary/90 py-1 pl-1"
                : "bg-background text-foreground hover:bg-accent py-1.5 pl-1.5",
            )}
          >
            <img
              src={scrolled ? wendelAvatarGrey : wendelAvatar}
              alt="Wendel Santos"
              className={cn(
                "shrink-0 rounded-full object-cover",
                scrolled ? "h-7 w-7" : "h-9 w-9",
              )}
            />
            <span className="flex flex-col items-start leading-tight">
              <span>{ctaButton.label}</span>
            </span>
          </a>
        )}
        {ctaButton && (
          <a
            href="#lets-talk"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("lets-talk");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              else if (ctaButton.onClick) ctaButton.onClick();
            }}
            className={cn(
              "inline-flex items-center gap-2 rounded-full bg-primary py-1 pl-1 pr-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90 sm:hidden",
              mobileOpen && "hidden",
            )}
          >
            <img
              src={wendelAvatarGrey}
              alt="Wendel Santos"
              className="h-6 w-6 shrink-0 rounded-full object-cover"
            />
            <span>Book a Meeting</span>
          </a>
        )}
        <button
          type="button"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileOpen((v) => !v)}
          className="inline-flex items-center justify-center rounded-md p-2 text-foreground hover:bg-accent lg:hidden"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
    </header>
  );

  const MOBILE_NAV = [
    { label: "My Work", href: "#work", icon: Briefcase },
    { label: "About", href: "#about", icon: User },
    { label: "Services", href: "/services", icon: LayoutTemplate },
    { label: "Pricing", href: mobilePricingHref ?? "/#pricing", icon: DollarSign },
    { label: "Questions", href: "#faq", icon: HelpCircle },
    { label: "Contact", href: "#contact", icon: Mail },
  ];

  const mobileMenu =
    mounted && mobileOpen
      ? createPortal(
          <div className="fixed inset-0 z-[150] lg:hidden">
            <div
              className="absolute inset-0 bg-background/80 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            <div className="absolute inset-x-4 top-20 max-h-[calc(100vh-6rem)] overflow-y-auto rounded-3xl border border-border bg-background shadow-2xl animate-in slide-in-from-top-4 fade-in duration-200">
              <div className="grid grid-cols-2 gap-2 px-6 py-5">
                {MOBILE_NAV.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={(e) => {
                        const href = item.href;
                        if (href.startsWith("#")) {
                          e.preventDefault();
                          const id = href.slice(1);
                          setMobileOpen(false);
                          if (document.getElementById(id)) {
                            setTimeout(() => scrollToAnchor(id), 50);
                          } else {
                            // Section not on this page — go to home and scroll there.
                            navigate({ to: "/", hash: id });
                            const start = Date.now();
                            const tryScroll = () => {
                              if (document.getElementById(id)) {
                                scrollToAnchor(id);
                              } else if (Date.now() - start < 2000) {
                                requestAnimationFrame(tryScroll);
                              }
                            };
                            requestAnimationFrame(tryScroll);
                          }
                        } else {
                          setMobileOpen(false);
                        }
                      }}
                      className="flex items-center justify-start gap-2 rounded-lg px-3 py-3 text-left text-base font-medium text-foreground/70 hover:bg-accent hover:text-foreground"
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      {item.label}
                    </a>
                  );
                })}
                {ctaButton && (
                  <a
                    href="#lets-talk"
                    onClick={(e) => {
                      e.preventDefault();
                      setMobileOpen(false);
                      setTimeout(() => {
                        const el = document.getElementById("lets-talk");
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        else if (ctaButton.onClick) ctaButton.onClick();
                      }, 50);
                    }}
                    className="col-span-2 mx-auto mt-2 flex w-[calc(100%-50px)] items-center justify-center gap-2 rounded-full bg-primary py-1 pl-1 pr-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                  >
                    <img
                      src={wendelAvatarGrey}
                      alt="Wendel Santos"
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    Book a Meeting
                  </a>
                )}
                <a
                  href="#lets-talk"
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileOpen(false);
                    setTimeout(() => {
                      const el = document.getElementById("lets-talk");
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 50);
                  }}
                  className="col-span-2 mx-auto flex w-[calc(100%-50px)] items-center justify-center rounded-full bg-secondary py-3 text-sm font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80"
                >
                  Get a Free Mockup
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 border-t border-border px-6 py-5">
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    onClick={() => setMobileOpen(false)}
                    className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background transition-transform hover:scale-110"
                    style={{ color: s.color }}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  const stickyNav =
    mounted && scrolled
      ? createPortal(
          <div
            data-sticky-nav
            className="fixed inset-x-0 top-4 z-[200] flex w-full justify-center px-4 animate-in slide-in-from-top-8 fade-in duration-300"
          >
            {nav}
          </div>,
          document.body,
        )
      : null;

  const mobileHeaderOverlay =
    mounted && mobileOpen && !scrolled
      ? createPortal(
          <div
            data-sticky-nav
            className="fixed inset-x-0 top-0 z-[200] mx-auto flex w-full justify-center lg:hidden"
          >
            {nav}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {scrolled && <div aria-hidden style={{ height: inlineHeight }} className="w-full" />}
      {!scrolled && (
        <div ref={inlineRef} className="relative z-30 mx-auto flex w-full justify-center">
          {nav}
        </div>
      )}

      {stickyNav}
      {mobileHeaderOverlay}
      {mobileMenu}
    </>
  );
}
